import React, { Component } from 'react'
import { filter, map, get, omit, each, find, isUndefined, findIndex, includes, isNull, isEmpty } from 'lodash'
import arrayMove from 'array-move'
import axios from 'axios'
import AWS from 'aws-sdk'
import { eachLimit } from 'async'
import moment from 'moment'
import MD5 from 'md5-es'
import { isAdmin, getImageWidth } from '../../utils'
import Snackbar from '../../components/Snackbar'
import SingleSubmission from './single-submission'
import SingleSubmissionInstagram from './single-submission-instagram'
import { verifyImage, uniqueFilename } from './utils'
import axiosInstance, { wpAxios } from '../../config/axiosResolver'
import ConfigResolver from '../../config/ConfigResolver'
import { statusToEmail } from '../../constants'
import { defaultSectionData } from './components/AdditionalInfo/constants'

const config = ConfigResolver.config()

/**
 * The fields whose failed write can make the writing tools draft from a stale
 * row -- exactly the Basic Information fields an editor blurs.
 *
 * saveSubmission also carries DERIVED writes queued by the status callback:
 * denied_date, accepted_date, a status of its own. No form control ever calls
 * saveSubmission for those, so a failure there could never be retried and
 * nothing could clear it: one failed accepted_date blocked every Copy tools
 * action for the rest of the session, telling the editor to save a change
 * they have no way to reach. They also feed none of the drafting -- a title
 * is written from the title, location, type, season and description -- so
 * their failure is no reason to refuse to draft.
 */
const ASSIST_SOURCE_FIELDS = ['title', 'type', 'location', 'season', 'description']

class SingleSubmissionContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      loadingValue: 0,
      isValid: true,
      isAdmin: isAdmin(this.props.user),
      message: null,
      variant: null,
      saveNotice: { open: false, variant: 'success', message: '' },
      submission: {},
      additionalInfo: {},
      galleryImages: [],
      credits: [],
      images: [],
      videos: [],
      videoError: false,
      gallery: {},
      galleryVendor: {},
      notes: [],
      colors: [],
      settings: [],
      styles: [],
      options: [],
      acceptedFiles: [],
      deniedFiles: [],
      instagramFavorites: [],
      instagramTypes: [],
      credentials: {},
      vendor: { name: '', type: '', url: '', instagram: '', email: '' },
      venue: { name: '', type: '', url: '', instagram: '', email: '' },
      path: 'https://sharp.stylemepretty.com',
      isLbb: false,
      isProfile: false,
      post: [],
      nextSubmission: null,
      statistics: {},
      workshopData: {},
      showReferralModal: false,
      submitterVendorId: null,
      // Bumped only when an ACCEPTED copy edit lands, never on an ordinary
      // save response. The AdditionalInfo form remounts on it, so an external
      // rewrite refreshes the fields while normal typing is left alone.
      appliedVersion: 0,
      // STY-624: bumped ONLY when the title tool applies a title, never on an
      // ordinary save. GeneralInfo remounts on it, so an externally chosen
      // title refreshes the field while the editor's own typing is left alone.
      // Keying that form on submission.title instead looked equivalent and was
      // not: the title's OWN save changes it too, so a slow PUT remounted the
      // form under an editor who had moved on to another field and threw away
      // what they had typed. Same trap the STY-579 comment describes.
      appliedTitleVersion: 0
    }

    this.id = this.props.match.params.id
    // Serialized additional-info writes: latest queued payload, and the
    // in-flight drain promise. See saveSubmissionDetails.
    this.pendingSave = null
    this.saveRunner = null
    // Outcome of the most recently COMPLETED write, not a tally of failures.
    // Every payload is a full snapshot, so this is what says whether the
    // stored document is current.
    this.lastSaveError = null
    // Ordinary submission-row PUTs in flight. Separate from pendingSave,
    // which is the additional-info queue: the two write different things
    // through different APIs, and a flush has to wait for both.
    this.pendingRowSaves = []
    // Failed row writes, KEYED BY FIELD, kept beyond the promise's own
    // lifetime: a PUT that failed just before a flush is already out of
    // pendingRowSaves, and the flush still needs to know.
    //
    // Keyed rather than a single slot because a row PUT sends the whole
    // submission built from this.state.submission, which is only updated from
    // a SUCCESSFUL response. So a title write that fails leaves state holding
    // the old title, and a type write started afterwards carries that old
    // title too -- it succeeds without ever containing the edit that failed.
    // A single slot let that unrelated success clear the title's error, and
    // drafting then read a row whose title the editor had already changed.
    // Only a save of the SAME field can clear that field's failure.
    this.rowSaveErrors = {}
    // The most recently ISSUED write per field. Two saves of one field can
    // complete out of order -- a second title edit fails, then the first
    // title PUT lands -- and without this the older success deleted the newer
    // failure, so drafting proceeded against a title the editor had already
    // replaced. Only the newest request for a field may speak for it.
    this.rowSaveSeq = {}
    // Row writes run one after another; see saveSubmission.
    this.rowChain = Promise.resolve()
    this.rowSaveFailed = this.rowSaveFailed.bind(this)
    // The payload of that failed write, kept so a flush can offer it again.
    this.failedPayload = null
    this.deleteCredit = this.deleteCredit.bind(this)
    this.addCredit = this.addCredit.bind(this)
    this.addVideo = this.addVideo.bind(this)
    this.saveSubmission = this.saveSubmission.bind(this)
    this.applyTitle = this.applyTitle.bind(this)
    this.saveSubmissionDetails = this.saveSubmissionDetails.bind(this)
    this.flushSaves = this.flushSaves.bind(this)
    this.showSaveNotice = this.showSaveNotice.bind(this)
    this.closeSaveNotice = this.closeSaveNotice.bind(this)
    this.refreshPreview = this.refreshPreview.bind(this)
    this.deleteSubmission = this.deleteSubmission.bind(this)
    this.onDrop = this.onDrop.bind(this)
    this.addNote = this.addNote.bind(this)
    this.submitForReview = this.submitForReview.bind(this)
    this.importSubmission = this.importSubmission.bind(this)
    this.createPost = this.createPost.bind(this)
    this.updatePost = this.updatePost.bind(this)
    this.emailSubmitter = this.emailSubmitter.bind(this)
    this.deleteImage = this.deleteImage.bind(this)
    this.deleteAllImages = this.deleteAllImages.bind(this)
    this.deleteVideo = this.deleteVideo.bind(this)
    this.updateImageOrder = this.updateImageOrder.bind(this)
    this.updateSubmissionImageIsFavorite = this.updateSubmissionImageIsFavorite.bind(this)
    this.updateSubmissionImageDescription = this.updateSubmissionImageDescription.bind(this)
    this.updateImageCredit = this.updateImageCredit.bind(this)
    this.acceptSocial = this.acceptSocial.bind(this)
    this.declineSocial = this.declineSocial.bind(this)
  }

  componentDidMount () {
    const hash = get(this.props, 'match.params.publicHash', '')
    const isReviewing = window.location.search === '?review=review'
    const isQuickReviewing = window.location.search === '?review=quick'
    this.startTimer()
    Promise.all([
      axiosInstance.get(`/submissions/${this.id}/`),
      this.id > 358705 ? wpAxios.get(`${config.siteUrl}/api/v2/submissions/${this.id}/get-additional-info`) : null,
      axiosInstance.get(`/submissions/${this.id}/credits/`),
      axiosInstance.get(`/submissions/${this.id}/images/`),
      axiosInstance.get(`/submissions/${this.id}/videos/`),
      axiosInstance.get(`/submissions/${this.id}/notes/`),
      config.smpCognito ? wpAxios.get(`${config.siteUrl}/api/v2/submissions/credentials/${this.id}${hash ? '/public/' + hash : ''}`) : axiosInstance.get(`/submissions/${this.id}/cognito/`),
      axiosInstance.get(`/term-taxonomies/?taxonomy=color`),
      axiosInstance.get(`/term-taxonomies/?taxonomy=style`),
      axiosInstance.get(`/term-taxonomies/?taxonomy=setting`),
      axiosInstance.get(`/submission-options/`),
      wpAxios.get(`${config.siteUrl}/api/v2/submissions/${this.id}/get-post`),
      wpAxios.get(`${config.siteUrl}/api/v2/submissions/${this.id}/workshop-data`)
    ])
      .then(([submission, additionalInfo, credits, images, videos, notes, credentials, colors, styles, settings, options, post, workshopData]) => {
        this.setState({ 
          submission: this.formatSubmission(submission.data),
          additionalInfo: this.id > 358705 ? additionalInfo.data.data : {},
          credits: credits.data || [],
          images: images.data,
          videos: videos.data,
          notes: notes.data,
          credentials: config.smpCognito ? credentials.data.data.credentials : credentials.data,
          colors: colors.data || [],
          styles: styles.data || [],
          settings: settings.data || [],
          options: options.data,
          post: post.data.data || [],
          workshopData: workshopData.data.data || {}
        })
      })
      .finally(() => {
        this.getStatistics()
        this.setState({ isLoading: false, path: this.state.submission.image_server ? `https://${this.state.submission.image_server}.stylemepretty.com` : this.state.path })
        if (this.state.credits.length) {
          this.getIsLBB()
          this.fetchVendorEmails() // Fetch emails for all vendors
        }
        if (this.state.submission.status === 'collection') this.getGallery(this.state.submission.gallery_id)
        if (this.state.submission.type === 'lbb-instagram') this.getFavorites(this.state.images)
        if (isReviewing || isQuickReviewing) this.getNextSubmission(this.state.submission.id, isReviewing ? 'review' : 'quick')
        this.getSubmitterIsLbb()
        if (this.state.submission.gallery_id) {
          wpAxios.get(`${config.siteUrl}/api/v2/gallery/${this.state.submission.gallery_id}`)
            .then((res) => {
              this.setState({ galleryImages: res.data.data })
            })
        }
        const defaults = find(defaultSectionData, ['type', this.state.submission.type])
        if (isEmpty(this.state.additionalInfo) && !this.state.isAdmin && this.id > 358705 && defaults && this.state.submission.status !== 'collection') {
          this.saveSubmissionDetails({ type: 'default', leadImage: null, sections: get(defaults, 'sections', []), title: get(defaults, 'title', ''), subtitle: get(defaults, 'subtitle', '') }, { silent: true })
        }
      })
      .catch(() => {
        if(get(this.props, 'user.email')) this.props.history.push(`/submissions/dashboard/${this.props.user.email}`)
        else window.location.href = `${config.siteUrl}/login?redirect_to=/submissions/&msg=You+must+have+an+account+to+create+a+submission.`
      })
  }

  refreshPreview () {
    wpAxios.get(`${config.siteUrl}/api/v2/submissions/${this.id}/get-additional-info`)
      .then((res) => {
        this.setState({ additionalInfo: res.data.data })
      })
  }

  getNextSubmission (id, type) {
    wpAxios.get(`${config.siteUrl}/api/v2/submissions/${id}/get-next-submission/${type}`)
      .then((res) => {
        this.setState({ nextSubmission: res.data.data })
      })
  }

  startTimer () {
    this.timer = setInterval(() => {
      config.smpCognito ? wpAxios.get(`${config.siteUrl}/api/v2/submissions/credentials/${this.id}`) : axiosInstance.get(`/submissions/${this.id}/cognito/`)
        .then((res) => {
          this.setState({ credentials: config.smpCognito ? res.data.data.credentials : res.data })
        })
    }, 1800000)
  }

  getSubmitterIsLbb () {
    wpAxios.get(`${config.siteUrl}/api/v2/vendor/user/${this.state.submission.submit_email}`)
      .then((res) => {
        if (get(res, 'data.data.vendor_profile.is_active', 0) === '1') this.setState({ isProfile: true })
        if (get(res, 'data.data.lbb', false) === '1' && get(res, 'data.data.is_active', false) === '1') {
          this.setState({ 
            isLbb: true,
            submitterVendorId: get(res, 'data.data.id', null)
          })
        }
      })
  }

  getFavorites (images) {
    map(images, (image) => {
      axiosInstance.get(`/favorites/?object_type=lbb_instagram&object_id=${image.id}&user_id=${image.id}`)
        .then((result) => {
          axiosInstance.get(`/metadata/?object_type=image&object_id=${image.id}&meta_key=instagram_type`)
            .then((res) => {
              if (result.data.count) this.setState({ instagramFavorites: [image.id, ...this.state.instagramFavorites]})
              if (res.data.results.length) this.setState({ instagramTypes: [res.data.results[0], ...this.state.instagramTypes] })
            })
        })
    })
  }

  declineSocial () {
    this.saveSubmission('status', 'social-denied')
  }

  acceptSocial () {
    this.saveSubmission('status', 'social')
  }

  getGallery (id) {
    axios
      .get(`${config.siteUrl}/api/portfolio-gallery/${id}`)
      .then((res) => {
        this.setState({ gallery: res.data })
        this.getVendor(get(res, 'data.vendor_id'))
          .then((res) => {
            this.setState({ galleryVendor: res.data })
          })
      })
  }

  getIsLBB (newCredit = false, newLocation = false) {
    let credits = this.state.credits
    if(newCredit) {
      if (newCredit.vendor) {
        axios
          .get(`${config.siteUrl}/api/v2/vendor/${newCredit.vendor}/submission-credit`)
          .then((res) => {
            if(get(res, 'data.data.lbb')) credits[newLocation].isLbb = true
            else credits[newLocation].isLbb = false
            if(get(res, 'data.data.profile')) credits[newLocation].isProfile = true
            else credits[newLocation].isProfile = false
            if(get(res, 'data.data.instagram', '')) credits[newLocation].instagram = get(res, 'data.data.instagram', '')
            if(get(res, 'data.data.email', '')) credits[newLocation].email = get(res, 'data.data.email', '')
          })
          .finally(() => {
            this.setState({ credits: credits })
          })
      }
    } else {
      map(credits, (credit, key) => {
        if (credit.vendor) {
          axios
            .get(`${config.siteUrl}/api/v2/vendor/${credit.vendor}/submission-credit`)
            .then((res) => {
              if(get(res, 'data.data.lbb')) credits[key].isLbb = true
              else credits[key].isLbb = false
              if(get(res, 'data.data.profile')) credits[key].isProfile = true
              else credits[key].isProfile = false
              if(get(res, 'data.data.instagram', '')) credits[key].instagram = get(res, 'data.data.instagram', '')
              if(get(res, 'data.data.email', '')) credits[key].email = get(res, 'data.data.email', '')
            })
            .finally(() => {
              this.setState({ credits: credits })
            })
        }
      })
    }
  }

  fetchVendorEmails () {
    const creditsWithVendors = filter(this.state.credits, credit => credit.vendor)
    
    Promise.all(
      map(creditsWithVendors, (credit) => {
        // Fetch vendor details to get email
        return wpAxios.get(`${config.siteUrl}/api/vendor/dashboard/${credit.vendor}`)
          .then(res => {
            const vendorEmail = get(res, 'data.email', '')
            return {
              creditId: credit.id,
              email: vendorEmail
            }
          })
          .catch(err => {
            return {
              creditId: credit.id,
              email: ''
            }
          })
      })
    ).then(emailResults => {
      // Update credits with fetched emails
      const updatedCredits = this.state.credits.map(credit => {
        const emailResult = find(emailResults, ['creditId', credit.id])
        if (emailResult && emailResult.email) {
          return { ...credit, email: emailResult.email }
        }
        return credit
      })
      
      this.setState({ credits: updatedCredits })
    })
  }

  getStatistics () {
    wpAxios.get(`${config.siteUrl}/api/v2/submissions/vendor/${this.state.submission.submit_email}/submissions-data`)
      .then((res) => this.setState({ statistics: res.data.data }))
      .catch((err) => console.log(err))
  }

  getVendor (vendorId) {
    return axios
      .get(`${config.siteUrl}/api/vendor/dashboard/${vendorId}`)
  }
  
  formatSubmission (submission) {
    const fields = ['order', 'credits', 'colors', 'settings', 'styles']
    let sub = submission
    if(!submission.json) sub.json = '{}'
    each(fields, (field) => {
      if(!submission[field]) sub[field] = []
    })
    return sub
    
  }

  onSortEnd = ({oldIndex, newIndex, collection}) => {
    let vendors = filter(this.state.credits, ['category', 't:vendor'])
    let venues = filter(this.state.credits, ['category', 't:venue'])
    if(collection === 0) vendors = arrayMove(vendors, oldIndex, newIndex)
    else venues = arrayMove(venues, oldIndex, newIndex)
    Array.prototype.push.apply(vendors, venues)
    this.setState({ credits:  vendors}, () => {
      this.updateCreditOrder(this.props.match.params.id)
    })
  }

  updateCreditOrder (submissionId) {
    axiosInstance
      .put(`/submissions/${submissionId}/`, { 
        ...this.state.submission,
        credits: map(this.state.credits, 'id')
      })
  }

  updateImageOrder (oldIndex, newIndex) {
    if (oldIndex !== newIndex) {
      let images = arrayMove(this.state.images, oldIndex, newIndex)
      this.setState({ images: images })
      axiosInstance
        .put(`/submissions/${this.id}/`, {
          ...this.state.submission,
          order: map(images, 'id')
        })
        .then(() => {
          if (this.state.submission.gallery_id && this.state.submission.status === 'collection') {
            this.updateGalleryOrder(this.state.gallery, images)
            this.setCoverPhoto(this.state.gallery, images)
          }
        })
        .finally(() => {
          this.saveGallery()
        })
    }
  }

  updateImageCredit (values, imageId) {
    const image = find(this.state.images, { 'id': imageId })
    if (!isUndefined(image)) {
      axiosInstance
        .put(`/submission-images/${imageId}/`, {
          ...image,
          credit_name: values.vendorName,
          credit_url: values.vendorUrl
        })
        .then((res) => {
          const index = findIndex(this.state.images, { id: imageId })
          let images = this.state.images
          images[index] = res.data
          this.setState({ images: images })
        })
    }
  }

  setCoverPhoto(gallery, images) {
    axios
      .put(`${config.siteUrl}/api/vendor/${gallery.vendor_id}/gallery/${this.state.submission.gallery_id}/set-main`, {
        id: find(gallery.images, { filename: images[0].filename }).id
      })
  }

  updateGalleryOrder (gallery, images) {
    axios
      .put(`${config.siteUrl}/api/portfolio-gallery/${this.state.submission.gallery_id}/images`, {
        images: map(get(gallery, 'images'), (image, i) => {
          return { 
            id: image.id,
            position: findIndex(images, { filename: image.filename })
          }
        })
      })
  }

  deleteCredit (values) {
    if (this.state.submission.status === 'collection') {
      this.deleteGalleryCredit(values)
    } else {
      axiosInstance
        .delete(`/submission-credits/${values.id}/`)
        .then(() => {
          let credits = filter(this.state.credits, function(c) { return get(c, 'id') !== values.id})
          this.setState({ credits: credits }, () => {
            this.updateCreditOrder(this.props.match.params.id)
          })
        })
    }
  }

  deleteGalleryCredit (values) {
    axios
      .delete(`${config.siteUrl}/api/portfolio-credit/${values.id}`)
      .then(() => {
        let credits = filter(this.state.credits, function(c) { return get(c, 'id') !== values.id})
        this.saveGallery()
        this.setState({ credits: credits }, () => {
          this.updateCreditOrder(this.props.match.params.id)
        })
      })
  }

  addCredit (credit) {
    const type = credit.category.slice(2)
    this.setState({ [type]: { name: '', type: '', url: '', instagram: '', email: '' } })
    let c = credit
    let url = get(c, 'url')
    if(!url.startsWith('http://') && !url.startsWith('https://') && url.length !== 0) url = `http://${get(c, 'url')}`
    c.url = url
    if (this.state.submission.status === 'collection') {
      this.addCreditToGallery(c)
    } else {
      axiosInstance
        .post(`/submissions/${c.submission}/credits/`, {
          ...omit(c, ['id']) 
        })
        .then((result) => {
          let credits = this.state.credits
          credits.push(result.data)
          this.setState({ credits: credits }, () => {
            this.updateCreditOrder(c.submission)
            this.getIsLBB(c, credits.length - 1)
            this.createVendorForCredit(result.data.id, { instagram: get(c, 'instagram', ''), email: get(c, 'email', '') })
          })
        })
    }
  }
  
  createVendorForCredit (creditId, data, attempt = 1) {
    console.log(`[VendorCredit] attempt=${attempt} credit=${creditId} instagram=${data.instagram || 'none'} email=${data.email || 'none'}`)
    wpAxios.post(`${config.siteUrl}/api/v2/submissions/vendor/${creditId}`, data)
      .then((res) => {
        if (get(res, 'data.status') === 200 && get(res, 'data.data.id')) {
          const vendorId = get(res, 'data.data.id')
          console.log(`[VendorCredit] SUCCESS credit=${creditId} vendor=${vendorId} name=${get(res, 'data.data.name', '')}`)
          const credits = this.state.credits
          const idx = findIndex(credits, (c) => c.id === creditId)
          if (idx !== -1) {
            credits[idx].vendor = parseInt(vendorId, 10)
            this.setState({ credits })
          }
        } else if (attempt < 3) {
          console.warn(`[VendorCredit] RETRY credit=${creditId} attempt=${attempt} response:`, res.data)
          setTimeout(() => this.createVendorForCredit(creditId, data, attempt + 1), 2000 * attempt)
        } else {
          console.error(`[VendorCredit] FAILED credit=${creditId} after ${attempt} attempts`, res.data)
        }
      })
      .catch((err) => {
        if (attempt < 3) {
          console.warn(`[VendorCredit] NETWORK_ERROR credit=${creditId} attempt=${attempt}`, err.message || err)
          setTimeout(() => this.createVendorForCredit(creditId, data, attempt + 1), 2000 * attempt)
        } else {
          console.error(`[VendorCredit] FAILED credit=${creditId} after ${attempt} attempts`, err.message || err)
        }
      })
  }

  addCreditToGallery (credit) {
    axios
      .post(`${config.siteUrl}/api/portfolio-gallery/${this.state.submission.gallery_id}/credits`, {
        name: credit.name,
        type: credit.type,
        url: get(credit, 'url', ''),
        url_path_level: (get(credit, 'url', '').replace(/(^\w+:|^)\/\//, '').split('/').length - 1).toString()
      })
      .then((result) => {
        result.data.id = result.data.cid
        let credits = this.state.credits
        credits.push(result.data)
        this.saveGallery()
        this.setState({ credits: credits }, () => {
          this.updateCreditOrder(credit.submission)
        })
      })
  }

  submitForReview () {
    if(this.state.isValid) {
      const eligibleCredits = filter(this.state.credits, (credit) => !credit.isLbb && credit.email && credit.email.trim() !== '')
      
      // Check if user is LBB member and has vendor credits with emails
      const hasNonLbbVendorsWithEmail = this.state.isLbb && eligibleCredits.length > 0
      
      if (hasNonLbbVendorsWithEmail) {
        // Show referral modal first
        this.setState({ showReferralModal: true })
      } else {
        // Proceed with submission directly
        this.completeSubmission()
      }
    }
  }

  completeSubmission () {
    axiosInstance
      .put(`/submissions/${this.props.match.params.id}/`, { 
        ...this.state.submission,
        status: this.state.submission.status === 'accepted-quick' ? 'completed-quick' : 'review',
        submitted_date: moment().format('YYYY-MM-DD HH:mm:ss')
      })
      .then((result) => {
        this.setState({ submission: result.data })
      })
  }

  handleReferralModalClose = () => {
    this.setState({ showReferralModal: false })
    this.completeSubmission()
  }

  handleSendReferrals = (vendors) => {
    // The actual API call is handled in the modal component
  }

  importSubmission (createPost) {
    this.setState({ submission: { ...this.state.submission, isImporting: true }})
    
    if (this.props.match.params.id > 358705) {
      axios
        .post(`https://www.stylemepretty.com/wp-admin/admin-ajax.php?action=smp_import_submission&id=${this.props.match.params.id}`, { sub_order: map(this.state.images, 'id') })
        .then((res) => {
          this.setState({ submission: { ...this.state.submission, ...res.data }}, () => {
            if (!this.state.submission.post && createPost) this.createPost()
            else if (this.state.submission.post && createPost) this.updatePost()
          })})
        .catch((error) => this.importSubmission(createPost))
    } else {
      axios
        .post(`https://www.stylemepretty.com/wp-admin/admin-ajax.php?action=smp_import_submission&id=${this.props.match.params.id}${createPost ? '&post' : ''}`, { sub_order: map(this.state.images, 'id') })
        .then((res) => this.setState({ submission: { ...this.state.submission, ...res.data, isImporting: false }}))
        .catch((error) => this.importSubmission(createPost))
    }
  }

  createPost () {
    axios
      .post(`https://www.stylemepretty.com/wp-admin/admin-ajax.php?action=smp_import_post_submission&id=${this.props.match.params.id}&user_id=${this.props.user.id}`)
      .then((res) => this.setState({ submission: { ...this.state.submission, ...res.data }}, () => { this.updatePost() }))
      .catch((error) => this.createPost())
  }

  updatePost () {
    axios
      .post(`https://www.stylemepretty.com/wp-admin/admin-ajax.php?action=smp_import_post_submission&id=${this.props.match.params.id}&user_id=${this.props.user.id}`)
      .then((res) => this.setState({ submission: { ...this.state.submission, ...res.data, isImporting: false }}))
      .catch((error) => this.updatePost())
  }

  getYoutubeData = async (videoId) => {
    const data = {
      embed: `https://www.youtube.com/embed/${videoId}`,
      thumb: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
    }
    return data
  }

  getVimeoData = async (videoId) => {
    let result
    
    await axios.get(`https://vimeo.com/api/v2/video/${videoId}.json`)
      .then((res) => {
        result = {
          embed: `http://player.vimeo.com/video/${videoId}`,
          thumb: res.data[0].thumbnail_large
        }
      })
      .catch((err) => {
        console.log(err)
      })

    return result
  }

  async addVideo (video) {
    let result
    let yt = /.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#&?]*).*/
    let vimeo = /(?:.*vimeo.com\/)(?:video\/)?([0-9]+)/
    let match = yt.exec(video)
    
    if (match && match[1]) {
      result = await this.getYoutubeData(match[1])
    } else {
      match = vimeo.exec(video)
      result = await this.getVimeoData(match[1])
    }
    if(get(result, 'thumb')) {
      axiosInstance
        .post(`/submissions/${this.props.match.params.id}/videos/`, {
          url: video,
          submission: this.props.match.params.id,
          thumb: result.thumb,
          embed: result.embed
        })
        .then((result) => {
          this.setState({ videos: [result.data, ...this.state.videos], videoError: false })
        })
    } else {
      this.setState({ videoError: true })
    }
  }

  deleteVideo (videoId) {
    const url = `/submission-videos/${videoId}/`
    axiosInstance
      .delete(url)
      .then(() => {
        let videos = filter(this.state.videos, function(v) { return get(v, 'id') !== videoId})
        this.setState({ videos: videos })
      })
  }

  showSaveNotice (variant, message) {
    this.setState({ saveNotice: { open: true, variant: variant, message: message } })
  }

  closeSaveNotice () {
    this.setState({ saveNotice: { ...this.state.saveNotice, open: false } })
  }

  // `silent` suppresses the success toast for writes the user did not ask for
  // (the default-sections seed on mount). Failures always surface.
  //
  // Saves fire on every field blur, so several can be requested in quick
  // succession. This endpoint replaces the ENTIRE additional-info document, so
  // two overlapping writes can reach the server out of order and leave it
  // holding the older payload while the UI shows the newer one — the edit then
  // vanishes on reload, silently. Suppressing the stale *response* is not
  // enough; the stale *write* is the problem. So writes are serialized: at most
  // one is in flight, and because every payload is a full snapshot, a newer
  // queued write supersedes any still waiting (a burst of blurs collapses to a
  // single request carrying all of the edits).
  saveSubmissionDetails (data, { silent = false, baseHash = null } = {}) {
    // Each call gets its own promise, resolved when ITS payload lands.
    //
    // Returning the shared queue promise meant an apply was reported as failed
    // whenever ANY earlier queued save had failed — even though the
    // suggestion's own POST had succeeded and already shown a success notice.
    // The editor was sent down a retry path for a change that was saved.
    let settle
    let fail
    const outcome = new Promise((resolve, reject) => { settle = resolve; fail = reject })

    // A queued-but-unsent payload is superseded by this one. Its caller is
    // satisfied by the newer write, which contains everything it did.
    if (this.pendingSave && this.pendingSave.settle) this.pendingSave.settle()

    // baseHash rides with the payload rather than being held on the instance:
    // a queued write can be superseded by a newer one, and the newer document
    // is not the one this suggestion was generated against.
    this.pendingSave = { data: data, silent: silent, baseHash: baseHash, settle: settle, fail: fail }

    if (!this.saveRunner) {
      // The shared runner answers a different question: "is the stored document
      // current?" That is the outcome of the LAST write, which is what
      // flushSaves() needs before generating a copy edit from stored content.
      this.saveRunner = this.drainSaves()
        .then((lastError) => {
          this.saveRunner = null
          if (lastError) throw lastError
        })
        .catch((err) => {
          this.saveRunner = null
          throw err
        })

      // Nothing may await the runner directly, so keep an unhandled rejection
      // off the console; the real outcomes are reported per payload above and
      // to whoever calls flushSaves().
      this.saveRunner.catch(() => {})
    }

    // Most callers -- field blur, delete, template creation, the mount seed --
    // fire and forget. The outcome promise rejects on a failed write, and an
    // ignored rejection is an unhandled-rejection warning on every routine 401
    // or dropped connection, on top of the snackbar that already reported it.
    //
    // Marking it handled here does not hide anything: a caller that chains
    // .then/.catch still receives the rejection. Same pattern as saveRunner
    // just above.
    outcome.catch(() => {})

    return outcome
  }

  /**
   * STY-579: resolves once nothing is queued or in flight.
   *
   * Clicking a sidebar action blurs whatever field the editor was in, which
   * enqueues a save. Anything that then reads server state — the AI copy edit
   * generates its suggestion from the stored document — must wait, or it works
   * from content that is about to be replaced and its result silently
   * overwrites the edit that was still in flight.
   */
  flushSaves () {
    // BOTH queues. The additional-info runner below is only half the picture:
    // Basic Information writes go to Django through saveSubmission, and an
    // assist that reads the stored submission needs those to have landed too.
    const rows = this.pendingRowSaves.length
      ? Promise.all(this.pendingRowSaves.map(p => p.then(null, () => null)))
      : Promise.resolve()

    return rows.then(() => {
      // A failed row write is NOT swallowed, for the same reason the detail
      // queue's is not: the stored submission is then stale by exactly the
      // edit that did not land, and an assist reading it drafts from data the
      // editor has already changed. Swallowing it here let drafting proceed
      // as though the save had succeeded.
      // NOT cleared on the way past. Consuming it here meant the first Draft
      // click rejected and the second sailed through: no request pending, no
      // error left, and the assist drafted from the stale row anyway. It is
      // cleared by a successful save OF THAT FIELD and nothing else, which is
      // how lastSaveError behaves for the document queue.
      const failedField = Object.keys(this.rowSaveErrors)[0]
      if (failedField) throw this.rowSaveErrors[failedField]

      return this.flushDetailSaves()
    })
  }

  /** The additional-info queue alone. */
  flushDetailSaves () {
    // The rejection is NOT swallowed. A caller waiting on this needs to know
    // the queued save failed, because the stored document is then stale by
    // exactly the edit that did not land — and acting on it can overwrite work
    // the editor has already been told was lost.
    if (this.saveRunner) return this.saveRunner

    if (!this.lastSaveError) return Promise.resolve()

    // The last write failed, so the stored document is stale. Try it once more
    // before refusing: the common case is a transient 401 or a dropped
    // connection, and by the time the editor reaches for AI Copy Edit it has
    // usually recovered. If it fails again this rejects exactly as before.
    //
    // Retrying here rather than automatically, because a flush is a deliberate
    // act with something waiting on the answer; a background retry loop on a
    // persistent failure would just hammer the endpoint.
    if (this.failedPayload) {
      const retry = this.failedPayload
      this.failedPayload = null

      this.saveSubmissionDetails(retry.data, { silent: retry.silent })

      // The RUNNER, not that payload's own outcome. The editor can carry on
      // typing while the retry is in flight, and a blur behind it enqueues
      // another snapshot; the per-call promise settles as soon as the retry
      // lands, which would let the copy edit read a document the newer write
      // has not reached yet. The runner settles when the queue is empty.
      return this.saveRunner || Promise.resolve()
    }

    return Promise.reject(this.lastSaveError)
  }

  // Resolves with the outcome of the LAST write to complete, not with the
  // first failure seen. Every payload is a full snapshot of the whole
  // document, so a later write that lands already contains everything a failed
  // earlier one carried: the stored document is current and a copy edit may
  // safely be generated from it. Reporting the historical failure instead told
  // the editor to go and save work the server already had.
  drainSaves () {
    const next = this.pendingSave

    // Queue empty, so the most recent write is the final word on this drain.
    if (!next) return Promise.resolve(this.lastSaveError)

    this.pendingSave = null

    return this.postAdditionalInfo(next.data, next.silent, next.baseHash)
      .then(() => {
        this.lastSaveError = null
        this.failedPayload = null
        if (next.settle) next.settle()
        return this.drainSaves()
      })
      .catch((err) => {
        // A failed APPLY leaves the stored document exactly as it was, so it
        // is not stale and nothing needs retrying. Recording it as the last
        // save error meant an editor who accepted a suggestion, hit a
        // transport error and then declined was locked out of AI Copy Edit
        // entirely: every later flush rejected on an error about a write they
        // had already walked away from, with no payload left to retry, until
        // they happened to make and save some unrelated edit.
        //
        // Ordinary saves still set it, because those DO leave the document
        // behind the editor's screen.
        if (!next.baseHash) this.lastSaveError = err

        // Held so flushSaves() can offer it again. The field helpers mutate
        // additionalInfo BEFORE posting and the blur handlers only enqueue
        // when the displayed value differs from that mutated object, so
        // re-blurring the field that failed does not retry it -- the editor
        // had no way back and AI Copy Edit stayed blocked for good.
        // ORDINARY saves only. A failed suggestion apply carries baseHash, and
        // retaining that meant: accept, transport error, editor declines and
        // closes -- then the next AI Copy Edit flushes, silently retries the
        // payload they just rejected, and applies it. Consent does not survive
        // a retry queue. The apply reports its own failure through its per-call
        // promise, and the modal stays open for the editor to decide again.
        this.failedPayload = next.baseHash ? null : { data: next.data, silent: next.silent }
        if (next.fail) next.fail(err)
        // Keep draining. Making postAdditionalInfo reject (so the AI review can
        // tell a failed apply from a successful one) meant a failure short-
        // circuited this chain and left any NEWER queued payload unsent until
        // the editor happened to type again — so a transient 401 or dropped
        // connection could silently discard their latest work. The queue exists
        // precisely so one bad write does not strand the next one.
        return this.drainSaves()
      })
  }

  // Never rejects — a failed write must not stall the queue behind it.
  postAdditionalInfo (data, silent, baseHash = null) {
    // STY-577: the public share link authorizes by hash, so forward it when
    // we're on that route or the save will be rejected.
    const publicHash = get(this.props, 'match.params.publicHash', '')
    const url = `${config.siteUrl}/api/v2/submissions/${this.state.submission.id}/save-additional-info${publicHash ? `?public=${publicHash}` : ''}`
    // STY-577: must be wpAxios, not bare axios — wpAxios is what attaches the
    // Authorization header, and this endpoint now requires it.
    return wpAxios
      .post(url, baseHash ? { data: data, baseHash: baseHash } : { data: data })
      .then((res) => {
        // Only the write that empties the queue may publish its response. The
        // AdditionalInfo editors derive each payload from the current
        // additionalInfo, so adopting an intermediate echo would rebase the
        // next edit onto content that predates everything still queued — and
        // since this endpoint replaces the whole document, that edit would
        // then overwrite the queued work. Errors still surface immediately.
        if (this.pendingSave) return

        // baseHash is only ever sent by the copy-edit apply, so it doubles as
        // "this write came from outside the form". Bumping the version on
        // every save instead would reset whatever the editor is typing in
        // another field the moment an earlier field's save came back.
        this.setState(prev => ({
          additionalInfo: res.data.data,
          appliedVersion: baseHash ? prev.appliedVersion + 1 : prev.appliedVersion
        }))
        if (!silent) this.showSaveNotice('success', 'Saved')
      })
      .catch((err) => {
        // Previously this endpoint always succeeded, so a rejection was never
        // surfaced. It can now return 401/403, and a silently dropped save
        // means the editor loses work without knowing.
        const status = get(err, 'response.status')
        const message = status === 401
          ? 'Your session expired — reload the page and sign in again. Your last change was NOT saved.'
          : status === 403
            ? 'You do not have permission to edit this submission. Your last change was NOT saved.'
            : 'Save failed. Your last change was NOT saved — check your connection and try again.'
        this.showSaveNotice('error', message)

        // STY-579: rethrow. Swallowing here was fine while every caller was a
        // field blur that had already been told via the snackbar, but the AI
        // review needs to know: on failure it must keep the suggestion open so
        // the editor can retry, rather than closing on a save that never
        // happened.
        throw err
      })
  }

  /**
   * STY-624: RETURNS the request now, where it used to fire and forget.
   *
   * Every caller before this was a blur handler that ignored the result, so
   * returning nothing cost nothing. The title tool is the first caller that
   * needs to know: it reported "Title updated", cleared its busy state and
   * closed the modal the instant this was called, because Promise.resolve on
   * undefined settles immediately. A PUT that then failed left the editor
   * told their title was saved when it was not.
   *
   * Ignoring the return is still safe -- an unawaited rejection is exactly as
   * unhandled as it was before.
   */
  /**
   * A title chosen in the Title tab, as opposed to typed into the field.
   *
   * Only this path bumps appliedTitleVersion, because only this path changes
   * the title while the editor's attention is somewhere else.
   */
  applyTitle (title) {
    return Promise.resolve(this.saveSubmission('title', title))
      .then((result) => {
        this.setState(prev => ({ appliedTitleVersion: prev.appliedTitleVersion + 1 }))

        return result
      })
  }

  /**
   * Does this field have a write that failed and has not been retried?
   *
   * The blur guards skip a save when the value equals the stored one, so an
   * editor who reverted a failed edit left its error standing with no way to
   * clear it: every Copy tools action then refused for the rest of the
   * session, over a field whose displayed and stored values now agreed.
   */
  rowSaveFailed (name) {
    return !!this.rowSaveErrors[name]
  }

  saveSubmission (name, value) {
    let val = value
    // What had been written to each field when this call was made.
    //
    // The status handlers below queue DERIVED writes -- a publication date
    // schedules the post, a status of denied stamps denied_date -- and those
    // are enqueued when this write COMPLETES, so they land after anything the
    // editor did in the meantime. Pick a publication date, then change the
    // status to accepted before the date PUT returns, and the derived
    // "scheduled" overwrote the newer choice and fired its side effects.
    // Serializing made that deterministic rather than a race, so a derived
    // write now yields to any explicit write of the same field issued since.
    const issued = { ...this.rowSaveSeq }
    if (includes(['settings', 'styles', 'colors'], name)) val = map(val, (v) => v.toString())
    const url = `/submissions/${this.props.match.params.id}/`

    // SERIALIZED, and the payload is built when the write RUNS rather than
    // when it is queued.
    //
    // Every row PUT sends a whole submission spread from this.state.submission,
    // which is only updated from a response. Two quick edits therefore both
    // snapshotted the same pre-edit state, and whichever landed last reverted
    // the field carried only by the other -- two successful writes, no error
    // anywhere, and a stored row silently missing an edit the form still
    // displayed. Waiting on the chain means each write starts from the state
    // the previous one left.
    const send = () => axiosInstance
      .put(url, {
        ...this.state.submission,
        [name]: val
      })
      .then((result) => {
        const derived = (field, to) => {
          if ((this.rowSaveSeq[field] || 0) !== (issued[field] || 0)) return

          this.saveSubmission(field, to)
        }

        this.setState({ submission: result.data }, () => {
          if(name === 'status' && val !== 'scheduled') this.addNote(`I changed the status to ${val}`)
          if(name === 'status' && (val === 'scheduled' || val === 'denied')) {
            // STY-583: must be wpAxios, not bare axios — only wpAxios attaches
            // the Authorization header, and /recruit now requires it. Same
            // mistake as saveSubmissionDetails had in STY-577.
            wpAxios
              .post(`${config.siteUrl}/api/v2/submissions/recruit`, {
                credits: this.state.credits,
                submission: this.state.submission
              })
          }
          if (this.state.submission.status === 'collection' && name === 'title') this.saveGallery()
          if(name === 'status' && this.state.submission.status === 'denied' && isNull(this.state.submission.denied_date)) derived('denied_date', moment().format('YYYY-MM-DD HH:mm:ss'))
          if(name === 'status' && this.state.submission.status !== 'denied' && !isNull(this.state.submission.denied_date)) derived('denied_date', null)
          if(name === 'status' && includes(['accepted', 'written', 'prepped', 'scheduled', 'accepted-quick', 'social-awaiting-permission'], this.state.submission.status) && isNull(this.state.submission.accepted_date)) derived('accepted_date', moment().format('YYYY-MM-DD HH:mm:ss'))
          if(name === 'publication_date' && !isNull(val)) {
            this.addNote(`I scheduled the post for ${moment(val).format('MMMM Do, YYYY')}`)
            derived('status', 'scheduled')
          }
          if(name === 'status' && !isUndefined(find(statusToEmail, ['status', val]))) this.sendEmailOnStatusChange(val)
        })
      })

    // The status handlers above call saveSubmission again from inside the
    // setState callback -- denied_date, accepted_date, the scheduled status.
    // Those nested writes queue behind this one rather than racing it, which
    // is the whole point; they cannot deadlock on it because this request
    // settles when the handler returns, not when the nested write finishes.
    const request = this.rowChain.then(send, send)

    // A failed write must not poison every write after it, so the chain
    // itself carries no rejection -- the failure is recorded by field below.
    this.rowChain = request.then(null, () => null)

    // STY-624: registered so flushSaves() waits for it.
    //
    // flushSaves drained only the additional-info queue, so an editor who
    // changed a Basic Information field and went straight to Copy tools could
    // have the title or the location PUT still in flight while the assist read
    // the stored row -- and draft from the previous location.
    //
    // Rejection is swallowed HERE only: this promise exists to say "the write
    // has finished", and saveSubmission's own callers already ignore failures
    // exactly as they did before.
    this.pendingRowSaves.push(request)

    const forget = () => {
      this.pendingRowSaves = this.pendingRowSaves.filter(p => p !== request)
    }

    const seq = (this.rowSaveSeq[name] || 0) + 1
    this.rowSaveSeq[name] = seq
    const newest = () => this.rowSaveSeq[name] === seq

    request.then(
      () => { if (newest()) delete this.rowSaveErrors[name]; forget() },
      (err) => {
        if (newest() && ASSIST_SOURCE_FIELDS.indexOf(name) !== -1) this.rowSaveErrors[name] = err

        forget()
      }
    )

    return request
  }

  sendEmailOnStatusChange (status) {
    const template = find(statusToEmail, ['status', status]).template
    // Guarded: same reach-through that blanked the page from Credits. Here it
    // would throw inside the send handler instead, losing the email with a
    // TypeError rather than a message an editor can act on.
    const raw = get(find(this.state.options, ['option_name', 'submission_email']), 'option_value')
    let templates = []

    try {
      templates = raw ? JSON.parse(raw) : []
    } catch (e) {
      templates = []
    }

    let message = get(find(templates, ['name', template]), 'value', '')

    // No template, no email. The lookup above falls back to an empty string,
    // and an empty string is a blank email to the vendor followed by a note
    // saying we sent them one -- worse than not sending, because it reads as
    // done. The note is where an editor will actually see this.
    if (!message) {
      this.addNote(`I could not send the ${template} email: that template is missing from the submission email settings.`)
      return
    }

    message = message.replace(/{year}/g, moment().format('YYYY')).replace(/{public_link}/g, `https://www.stylemepretty.com/submissions/submission/${this.id}/${MD5.hash('9cjJhA8' + this.id)}`).replace(/{submissionUrl}/g, `https://www.stylemepretty.com/submissions/submission/${this.id}`)
    const params = {
      emailFrom: 'submissions-help@stylemepretty.com',
      title: `Submission #${this.id}`,
      message: message,
      emailTo: this.state.submission.submit_email
    }
    this.emailSubmitter(params, template)
  }

  saveGallery () {
    if (!isNull(this.state.submission.gallery_id)) {
      axios
        .put(`${config.siteUrl}/api/portfolio-gallery/${this.state.submission.gallery_id}`, {
          title: this.state.submission.title,
          type: this.state.submission.type,
          description: this.state.submission.description,
          location: this.state.submission.location
        })
        .then((res) => {
          this.saveVendorGallery()
        })
    }
  }

  saveVendorGallery () {
    if (get(this.state, 'gallery.vendor_id')) {
      axios
        .put(`${config.siteUrl}/api/vendor/${this.state.gallery.vendor_id}/vendor_galleries`, {
          vendor_galleries: [{
            gallery_id: this.state.gallery.id,
            portfolio_show: 0,
            portfolio_sort: 0
          }]
        })
        .then((res) => {
          axios
            .post(`${config.siteUrl}/api/portfolio-gallery/${this.state.gallery.id}/cache`)
        })
        .catch((error) => {
          console.log(error)
        })
    }
  }

  deleteSubmission() {
    const url = `/submissions/${this.props.match.params.id}/`
    axiosInstance
      .put(url, {
        ...this.state.submission,
        status: 'trash'
      })
      .then(() => {
        this.props.history.push(`/submissions/dashboard/${this.props.user.email}`);
      })
  }

  uploadImagesToS3 (files) {
    let credentials = this.state.credentials
    if (!config.smpCognito) {
      credentials = {
        AccessKeyId: credentials.aws_access_key_id,
        SecretAccessKey: credentials.aws_secret_access_key,
        SessionToken: credentials.session_token
      }
    }

    AWS.config.update({
      region: config.region,
      credentials: {
        accessKeyId: credentials.AccessKeyId,
        secretAccessKey: credentials.SecretAccessKey,
        sessionToken: credentials.SessionToken
      }
    })

    const s3 = new AWS.S3({
      params: { 
        Bucket: config.bucket
      }
    })

    eachLimit(files, 5, (file, callback) => {
      const url = `${this.state.submission.path}/${file.name}`
      s3.upload({
        Key: url,
        Body: file,
        ACL: 'public-read'
      }, (error) => {
        if(error) {
          // sendDiscordError('Submissions Server', error, this.props.user, url)
        } else {
          this.addImageToSubmission(file, this.props.match.params.id)
            .then(file => {
              let accepted = this.state.acceptedFiles
              let index = accepted.findIndex(object => object.name === file.data.filename)
              if (this.state.submission.status === 'collection') this.addImageToGallery(get(this.state, 'submission.gallery_id'), file.data.filename, index)
              accepted[index].uploaded = true
              Object.assign(accepted[index], { id: file.data.id })
              accepted[index].path = `${this.state.submission.path}/${file.data.filename}`
              this.setState({ acceptedFiles: accepted })
              if (this.state.submission.type === 'lbb-instagram' && this.state.submission.status === 'started') this.saveSubmission('status', 'review')
              callback()
            })
        }
      })
    })
  }

  onDrop (acceptedFiles, rejectedFiles) {
    const uploads = map(acceptedFiles, async (file) => {
      let newFile = new File([file], uniqueFilename(file.name), { type: file.type })
      Object.assign(newFile, { original_filename: file.name })
      const preview = URL.createObjectURL(newFile)
      return await getImageWidth(preview).then((width) => {
        Object.assign(newFile, { preview: preview, width: width })
        return verifyImage(newFile, this.state.isAdmin)
      })
    })

    map(rejectedFiles, (fileObject) => {
      let file = fileObject.file
      let preview = null
      if (file.size <= 10485760) preview = URL.createObjectURL(file)
      let reason = ''
      if(file.size > 10485760) reason = 'is too large. Images must be smaller than 10 MB in file size.'
      else if (!file.type.includes('image')) reason = 'is not a valid format. Please upload images only.'
      else reason = 'was not uploaded due to an unknown error.'
      this.setState({ deniedFiles: [...this.state.deniedFiles, Object.assign(file, { reason: reason, preview: preview })] })
    })
    Promise.all(uploads).then((results) => {
      this.setState({
        acceptedFiles: [...filter(results, 'valid'), ...this.state.acceptedFiles],
        deniedFiles: [...this.state.deniedFiles, ...filter(results, (r) => { return !r.valid })],
        images: [...filter(results, 'valid'), ...this.state.images]
      }, () => {
        this.uploadImagesToS3(filter(results, 'valid'))
      })
    })
  }

  addImageToSubmission (file, submissionId) {
    return axiosInstance
      .post(`/submission-images/`, { 
        filename: file.name,
        original_filename: file.original_filename,
        submission: submissionId,
        exclude: 0
      })
  }

  addImageToGallery (galleryId, filename, index) {
    return axios
      .post(`${config.siteUrl}/api/portfolio-gallery/${galleryId}/images`, {
        filename: filename,
        position: index,
        skip: true
      })
      .then((res) => {
        this.saveGallery()
      })
  }

  updateSubmissionImageDescription (imageId, text) {
    const image = find(this.state.images, { 'id': imageId })
    if (!isUndefined(image)) {
      axiosInstance
        .put(`/submission-images/${imageId}/`, {
          ...image,
          description: text
        })
    }
  }

  updateSubmissionImageIsFavorite (imageId, isFavorite) {
    if(!isFavorite) {
      axiosInstance
        .post(`/favorites/`, { 
          object_type: 'lbb_instagram',
          object_id: imageId,
          video_id: null,
          user_id: imageId
        })
        .then((res) => {
          this.setState({ instagramFavorites: [imageId, ...this.state.instagramFavorites]})
        })
    } else {
      axiosInstance
        .delete(`/favorites/delete/`, { data: { user_id: imageId } } )
        .then((res) => {
          let favorites = filter(this.state.instagramFavorites, function(i) { return i !== imageId})
          this.setState({ instagramFavorites: favorites})
        })
    }
  }

  updateSubmissionImageMetadata (imageId, value) {
    return axiosInstance
      .post(`/metadata/`, {
        object_type: 'image',
        meta_value: value,
        object_id: imageId,
        meta_key: 'instagram_type'
      })
      .catch((error) => {
        return axiosInstance
          .put(`/metadata/put?object_id=${imageId}`, {
            object_type: 'image',
            meta_value: value,
            object_id: imageId,
            meta_key: 'instagram_type'
          }).catch((err) => {
            throw err
          })
      })
  }

  deleteImage (image) {
    const url = `/submission-images/${image.id}/`
    const imageId = get(find(get(this.state.gallery, 'images', []), { filename: image.filename }), 'id')
    let images = filter(this.state.images, function(i) { return get(i, 'id') !== image.id})
    this.setState({ images: images })
    
    if (this.state.submission.status === 'collection') {
      axios
        .delete(`${config.siteUrl}/api/portfolio-image/${imageId}`)
    }
    axiosInstance
      .delete(url)
  }

  deleteAllImages () {
    const url = `/submission-images/`
    const images = this.state.images
    this.setState({ images: [] })
    map(images, (image) => {
      const imageId = get(find(get(this.state.gallery, 'images', []), { filename: image.filename }), 'id')
      if (this.state.submission.status === 'collection') {
        axios
          .delete(`${config.siteUrl}/api/portfolio-image/${imageId}`)
      }   
      axiosInstance
        .delete(url + image.id + '/')
    })
  }

  downloadSubmission = (submissionId) => {
    const url = `/submissions/${submissionId}/download/`
    axiosInstance
      .get(url, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const totalLength = progressEvent.lengthComputable ? progressEvent.total : progressEvent.target.getResponseHeader('content-length') || progressEvent.target.getResponseHeader('x-decompressed-content-length')
          if (totalLength !== null) this.setState({ loadingValue: Math.round((progressEvent.loaded * 100) / totalLength ) })
          if (this.state.loadingValue === 100) this.setState({ loadingValue: 0 })
        }
      })
      .then((result) => {
        let blob = new Blob([result.data], {type: 'application/zip'})
        const downloadUrl = URL.createObjectURL(blob)
        let a = document.createElement('a')
        a.href = downloadUrl
        a.setAttribute('download', `smp-submission-${submissionId}.zip`)
        document.body.appendChild(a)
        a.click()
      })
  }

  addNote (note) {
    const url = `/submission-notes/`
    axiosInstance
      .post(url, {
        note: note,
        username: this.props.user.username,
        submission: this.props.match.params.id,
        assigned: null
      })
      .then((result) => {
        let notes = this.state.notes
        notes.unshift(result.data)
        this.setState({ notes: notes })
      })
  }

  emailSubmitter (params, templateName) {
    const url = `${config.siteUrl}/api/v2/submissions/email`
    wpAxios.post(url, {
      from: params.emailFrom,
      subject: params.title,
      message: params.message.replace(/\n/g, "<br />"),
      to: params.emailTo
    })
      .then((res) => {
        if (templateName) this.addNote(`I sent the submitter the ${templateName} email`)
        else this.addNote(`I emailed the submitter: ${params.message}`)
      })
      // A failed send wrote nothing at all, so the only signal was the absence
      // of a note nobody was looking for. The note is the record of what the
      // submitter was told; a send that did not happen belongs in it too.
      .catch(() => {
        this.addNote(templateName
          ? `The ${templateName} email to the submitter FAILED to send.`
          : `An email to the submitter FAILED to send: ${params.message}`)
      })
  }

  render () {
    let Component = this.state.submission.type !== 'lbb-instagram' ? SingleSubmission : SingleSubmissionInstagram

    return (
      <React.Fragment>
        <Component
          {...this.state}
          addCredit={this.addCredit}
          deleteCredit={this.deleteCredit}
          onSortEnd={this.onSortEnd}
          saveSubmission={this.saveSubmission}
          rowSaveFailed={this.rowSaveFailed}
          applyTitle={this.applyTitle}
          appliedTitleVersion={this.state.appliedTitleVersion}
          saveSubmissionDetails={this.saveSubmissionDetails}
          flushSaves={this.flushSaves}
          deleteSubmission={this.deleteSubmission}
          onDrop={this.onDrop}
          addNote={this.addNote}
          submitForReview={this.submitForReview}
          importSubmission={this.importSubmission}
          createPost={this.createPost}
          updatePost={this.updatePost}
          user={this.props.user}
          addVideo={this.addVideo}
          downloadSubmission={this.downloadSubmission}
          emailSubmitter={this.emailSubmitter}
          deleteImage={this.deleteImage}
          deleteAllImages={this.deleteAllImages}
          deleteVideo={this.deleteVideo}
          updateImageOrder={this.updateImageOrder}
          updateSubmissionImageMetadata={this.updateSubmissionImageMetadata}
          updateSubmissionImageIsFavorite={this.updateSubmissionImageIsFavorite}
          updateSubmissionImageDescription={this.updateSubmissionImageDescription}
          updateImageCredit={this.updateImageCredit}
          acceptSocial={this.acceptSocial}
          declineSocial={this.declineSocial}
          refreshPreview={this.refreshPreview}
          showReferralModal={this.state.showReferralModal}
          handleReferralModalClose={this.handleReferralModalClose}
          handleSendReferrals={this.handleSendReferrals}
          submitterVendorId={this.state.submitterVendorId}
        />
        <Snackbar
          open={this.state.saveNotice.open}
          variant={this.state.saveNotice.variant}
          message={this.state.saveNotice.message}
          handleClose={this.closeSaveNotice}
          hideDuration={this.state.saveNotice.variant === 'error' ? null : 2000}
        />
      </React.Fragment>
    )
  }
}

export default SingleSubmissionContainer

